<script>
import ListingResource from '../resources/listing'
import CompanyImage from '../components/CompanyImage.vue'
import JobPosting from '../components/JobPosting.vue'
import JobPostingApplyButton from '../components/JobPostingApplyButton.vue'
import JobPostingCell from '../components/JobPostingCell.vue'
import date from '@/mixins/date'

export default {
    name: 'JobListingPublic',
    components: {
        JobPosting,
        JobPostingApplyButton,
        CompanyImage,
        JobPostingCell
    },
    mixins: [date],
    watch: {
        '$route': function() {
            this.getListing()
        }
    },
    data() {
        return {
            loading: true,
            listingResource: null,
            relatedListingResources: []
        }
    },
    methods: {
        async getListing() {
            this.loading = true
            let listingSlug = this.$route.params.slug
            this.listingResource = await ListingResource.detail(listingSlug, { resolveRelated: true })
            this.loading = false
            let relatedResourcesResult = await this.listingResource.getRelatedPostings()
            this.relatedListingResources = relatedResourcesResult.resources
        },
        getRelatedFiltersFromResource(resource) {
            return {
                page_size: 3,
                type: resource.attributes.type,
                region__in: resource.attributes.regions.join(',')
            }
        }
    },
    mounted() {
        this.getListing()
    }
}
</script>

<template>
    <div>
        <div v-if="!loading">
            <section>
                <b-container>
                    <div class="job-title section-heading mb-0 mt-5">
                        <b-row>
                            <b-col sm="1">
                                <company-image :company-resource="listingResource.rel('company').resource" />
                            </b-col>
                            <b-col>
                                <h2>{{ listingResource }}</h2>
                                <p>at <strong v-page-title="`${String(listingResource)} at ${listingResource.get('company.name')}`">{{ listingResource.get('company.name') }}</strong></p>
                            </b-col>
                        </b-row>
                    </div>
                </b-container>
            </section>

            <section class="p-3 mt-5 gray">
                <b-container>
                    <b-row>
                        <b-col sm="4">
                            <dl class="mb-0">
                                <dt>Role</dt>
                                <dd>{{ listingResource.get('role.name') }} / {{ listingResource.get('type.name') }}</dd>
                            </dl>
                        </b-col>
                        <b-col sm="4">
                            <dl class="mb-0">
                                <dt>Remote Level</dt>
                                <dd>{{ listingResource.readonly.remote_level_pretty }}</dd>
                            </dl>
                        </b-col>
                        <b-col class="text-right">
                            <job-posting-apply-button class="mt-2" :listingResource="listingResource" />
                        </b-col>
                    </b-row>
                </b-container>
            </section>

            <section class="mt-5">
                <b-container>
                    <job-posting :listingResource="listingResource">
                        <template #before>
                            <h4>Company Description</h4>
                            <hr />
                            <p>{{ listingResource.get('company.description') }}</p>
                        </template>
                        <template #title>
                            <h4 class="mt-5">Job Description</h4>
                        </template>
                        <template #details>
                            <dl v-if="listingResource.getCompensationPretty() !== '$0 - $0' || !['', '0%', '0% to 0%'].includes(listingResource.getEquityPretty())">
                                <dt>Compensation</dt>
                                <dd v-if="listingResource.getCompensationPretty() !== '$0 - $0'">{{ listingResource.getCompensationPretty() }}</dd>
                                <dd v-if="!['', '0%', '0% to 0%'].includes(listingResource.getEquityPretty())">{{ listingResource.getEquityPretty() }} Equity</dd>
                            </dl>
                            <dl v-if="!!listingResource.get('location')">
                                <dt>Specific Location</dt>
                                <dd>{{ listingResource.get('location') }}</dd>
                            </dl>
                            <dl>
                                <dt>Regions</dt>
                                <dd v-for="(region, i) in listingResource.get('regions.name')" :key="i">{{ region }}</dd>
                            </dl>
                            <dl v-if="listingResource.attributes.markets && listingResource.attributes.markets.length > 0">
                                <dt>Markets</dt>
                                <dd v-for="(market, i) in listingResource.get('markets.name')" :key="i">{{ market }}</dd>
                            </dl>
                            <dl v-if="listingResource.attributes.skills && listingResource.attributes.skills.length > 0">
                                <dt>Skills</dt>
                                <dd v-for="(skill, i) in listingResource.get('skills.name')" :key="i">{{ skill }}</dd>
                            </dl>
                            <dl>
                                <dt>Posted</dt>
                                <dd>{{ dateStringToDatePretty(new Date(listingResource.get('created'))) }}</dd>
                            </dl>
                            </template>
                    </job-posting>
                </b-container>
            </section>

            <section class="p-5 mt-5 gray">
                <b-container>
                    <b-row>
                        <b-col>
                            <h3><strong>{{ listingResource.get('company.name') }}</strong> at a glance</h3>
                            <span v-if="listingResource.get('company.website')"><a :href="listingResource.get('company.website')" target="_blank"><b-icon icon="box-arrow-up-right" /> Visit Website</a></span>
                        </b-col>
                        <b-col md="2" v-if="listingResource.get('company.readonly_values').size_name">
                            <dl>
                                <dt>Employees</dt>
                                <dd>{{ listingResource.get('company.readonly_values').size_name }}</dd>
                            </dl>
                        </b-col>
                        <b-col md="2" v-if="listingResource.get('company.readonly_values').market_name">
                            <dl>
                                <dt>Market</dt>
                                <dd>{{ listingResource.get('company.readonly_values').market_name }}</dd>
                            </dl>
                        </b-col>
                        <b-col class="text-md-right">
                            <job-posting-apply-button class="mt-2" :listingResource="listingResource" />
                        </b-col>
                    </b-row>
                </b-container>
            </section>

            <section v-if="relatedListingResources.length > 0">
                <b-container>
                    <div class="section-heading text-center mb-0 mt-5"><p>Similar Jobs</p></div>
                    <job-posting-cell v-for="(resource, i) in relatedListingResources" :listing-resource="resource" :key="i" />
                </b-container>
            </section>
        </div>
        
        <div v-else>
            <section class="mt-5">
                <b-container>
                    <b-skeleton type="avatar" size="4em" rounded></b-skeleton>
                </b-container>
            </section>

            <section class="p-3 mt-5 gray">
                <b-container>
                    <b-skeleton width="80%"></b-skeleton>
                    <b-skeleton width="100%"></b-skeleton>
                </b-container>
            </section>

            <section class="mt-5">
                <b-container>
                    <b-card class="mb-3">
                        <b-skeleton width="85%"></b-skeleton>
                        <b-skeleton width="55%"></b-skeleton>
                        <b-skeleton width="100%"></b-skeleton>
                    </b-card>
                    <b-card class="mb-3">
                        <b-skeleton width="85%"></b-skeleton>
                        <b-skeleton width="55%"></b-skeleton>
                        <b-skeleton width="70%"></b-skeleton>
                        <b-skeleton width="55%"></b-skeleton>
                        <b-skeleton width="70%"></b-skeleton>
                        <b-skeleton width="55%"></b-skeleton>
                        <b-skeleton width="70%"></b-skeleton>
                    </b-card>
                    <b-card class="mb-3">
                        <b-skeleton width="85%"></b-skeleton>
                        <b-skeleton width="55%"></b-skeleton>
                        <b-skeleton width="100%"></b-skeleton>
                    </b-card>
                    <b-card class="mb-3">
                        <b-skeleton width="85%"></b-skeleton>
                        <b-skeleton width="55%"></b-skeleton>
                        <b-skeleton width="100%"></b-skeleton>
                    </b-card>
                    <b-card>
                        <b-skeleton width="85%"></b-skeleton>
                        <b-skeleton width="55%"></b-skeleton>
                        <b-skeleton width="70%"></b-skeleton>
                    </b-card>
                </b-container>
            </section>
        </div>

    </div>
</template>

<style lang="scss">
.section-heading strong {
    color: #212529;
}

.listing-details {
    max-height: 950px;
    overflow-y: scroll;
}
</style>
